// context/ThemeContext.jsx
import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(() => {
    const savedMode = localStorage.getItem('themeMode');
    return savedMode || 'system';
  });
  
  const [primaryColor, setPrimaryColor] = useState(() => {
    const savedColor = localStorage.getItem('primaryColor');
    return savedColor || '#1976d2';
  });

  const toggleColorMode = (newMode) => {
    setMode(newMode);
    localStorage.setItem('themeMode', newMode);
  };

  const handleSetPrimaryColor = (color) => {
    setPrimaryColor(color);
    localStorage.setItem('primaryColor', color);
  };

  // Detect system preference
  const getSystemMode = () => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  };

  const actualMode = mode === 'system' ? getSystemMode() : mode;

  // Create theme based on mode and primary color
  const theme = useMemo(() => {
    return createTheme({
      palette: {
        mode: actualMode,
        primary: {
          main: primaryColor,
        },
        secondary: {
          main: '#dc004e',
        },
        background: {
          default: actualMode === 'dark' ? '#0a0a0a' : '#f8fafc',
          paper: actualMode === 'dark' ? '#1e1e1e' : '#ffffff',
        },
      },
      typography: {
        fontFamily: '"Inter","Roboto","Helvetica","Arial",sans-serif',
        h1: {
          fontWeight: 700,
          fontFamily: '"Poppins","Roboto","Helvetica","Arial",sans-serif',
        },
        h2: {
          fontWeight: 600,
          fontFamily: '"Poppins","Roboto","Helvetica","Arial",sans-serif',
        },
        h3: {
          fontWeight: 600,
          fontFamily: '"Poppins","Roboto","Helvetica","Arial",sans-serif',
        },
        h4: {
          fontWeight: 600,
          fontFamily: '"Poppins","Roboto","Helvetica","Arial",sans-serif',
        },
        h5: {
          fontWeight: 500,
        },
        h6: {
          fontWeight: 500,
        },
      },
      shape: {
        borderRadius: 8,
      },
      components: {
        MuiButton: {
          styleOverrides: {
            root: {
              textTransform: 'none',
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: 12,
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              borderRadius: 12,
            },
          },
        },
      },
    });
  }, [actualMode, primaryColor]);

  // Listen for system theme changes
  useEffect(() => {
    if (mode === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => {
        // Force re-render by updating a state that triggers theme recreation
        setMode('system');
      };
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [mode]);

  const value = {
    mode,
    actualMode,
    toggleColorMode,
    primaryColor,
    setPrimaryColor: handleSetPrimaryColor,
  };

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};